import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import { useGetAllLeadsQuery } from "../../../Services/leads/LeadGeneration";
import { formatToIST } from "../../../components/reusableComponent/DateFormat";
import { toast } from "react-toastify";

const LeadGeneration = () => {
  const [leads, setLeads] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to get date string in YYYY-MM-DD format
  const getDateString = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  // Get default dates (1 month range)
  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    return {
      start: getDateString(startDate),
      end: getDateString(endDate),
    };
  };

  const defaultDates = getDefaultDates();

  // Filter states
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [status, setStatus] = useState("all");

  // Get today's date in YYYY-MM-DD format for max date validation
  const today = getDateString(new Date());

  // Helper function to parse state/city strings
  const parseLocation = (locationStr: string) => {
    if (!locationStr) return "";
    try {
      const jsonStr = locationStr.replace(/'/g, '"');
      const parsed = JSON.parse(jsonStr);
      return parsed.name || locationStr;
    } catch {
      return locationStr;
    }
  };

  const getApiStatus = (statusValue: string) => {
    return statusValue === "all" ? "" : statusValue;
  };

  // Export to CSV function
  const handleExportCSV = async () => {
    try {
      const loadingToast = toast.loading("Preparing CSV export...");

      // Build query parameters
      const queryParams = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });

      // Only add status if it's not "all"
      if (getApiStatus(status)) {
        queryParams.append("status", getApiStatus(status));
      }

      // Get API base URL dynamically
      const fetchingTenant = window.location.hostname.split(".")[0];
      const apiPort = process.env.REACT_APP_API_PROTOCOL;
      const API_BASE_URL = `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc`;

      const response = await fetch(
        `${API_BASE_URL}/leads/export/?${queryParams.toString()}`,
        {
          method: "GET",
          headers: {
            // Remove Content-Type header for GET request
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      if (!response.ok) {
        throw new Error(`Failed to export CSV: ${response.statusText}`);
      }

      // Check the content type to determine file format
      const contentType = response.headers.get("content-type");
      const contentDisposition = response.headers.get("content-disposition");

      // Get the blob
      const blob = await response.blob();

      // Validate blob
      if (blob.size === 0) {
        toast.warning("No data available to export");
        return;
      }

      // Determine file extension based on content type
      let fileExtension = "csv";
      let fileName = `leads_export_${new Date().toISOString().split("T")[0]}`;

      if (
        contentType?.includes("spreadsheet") ||
        contentType?.includes("excel")
      ) {
        fileExtension = "xlsx";
      } else if (contentDisposition) {
        // Try to extract filename from content-disposition header
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
          contentDisposition
        );
        if (matches && matches[1]) {
          const suggestedFileName = matches[1].replace(/['"]/g, "");
          fileName = suggestedFileName.split(".")[0];
          fileExtension = suggestedFileName.split(".").pop() || "csv";
        }
      }

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.setAttribute("href", url);
      link.setAttribute("download", `${fileName}.${fileExtension}`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      toast.success(`File exported successfully!`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export file. Please try again.");
    }
  };

  // Handler functions that reset page to 1 when filters change
  const handleStartDateChange = (newStartDate: string) => {
    setPage(1); // Reset to first page BEFORE updating filter
    setStartDate(newStartDate);
  };

  const handleEndDateChange = (newEndDate: string) => {
    setPage(1); // Reset to first page BEFORE updating filter
    setEndDate(newEndDate);
  };

  const handleStatusChange = (newStatus: string) => {
    setPage(1); // Reset to first page BEFORE updating filter
    setStatus(newStatus);
  };

  const {
    data: LeadGenerationData,
    isFetching,
    refetch,
  } = useGetAllLeadsQuery({
    page,
    page_size: pageSize,
    start_date: startDate,
    end_date: endDate,
    status: getApiStatus(status),
  });

  useEffect(() => {
    if (isFetching) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isFetching]);

  // Update leads and total count when data changes
  useEffect(() => {
    if (LeadGenerationData) {
      setLeads(LeadGenerationData.results || []);
      setTotalCount(LeadGenerationData.count || 0);
    }
  }, [LeadGenerationData]);

  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalCount);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  const getStatusColor = (status: string | undefined) => {
    const statusLower = status?.toLowerCase() || "pending";
    switch (statusLower) {
      case "pending":
        return { bgcolor: "#FFF3CD", color: "#856404" };
      case "forwarded":
        return { bgcolor: "#D4EDDA", color: "#155724" };
      case "followed_up":
        return { bgcolor: "#CCE5FF", color: "#004085" };
      default:
        return { bgcolor: "#F0F0F0", color: "#333" };
    }
  };

  const formatStatusLabel = (status: string | undefined) => {
    if (!status) return "Pending";
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.3)",
          zIndex: 9999,
        }}
      >
        <Typography sx={{ color: "#ff6b35", fontFamily: "Karbon" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Karbon",
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Box
        className="d-flex justify-content-between align-items-center"
        sx={{
          bgcolor: "#fff",
          px: 4,
          py: 3,
          borderBottom: "1px solid #f0e5e0",
        }}
      >
        <Typography variant="h5" color="#666666" fontFamily="Karbon">
          Lead Generation Management
        </Typography>

        <Button
          variant="contained"
          onClick={handleExportCSV}
          sx={{
            bgcolor: "#ff6b35",
            color: "#fff",
            fontFamily: "Karbon",
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(255,107,53,0.3)",
            "&:hover": {
              bgcolor: "#e55a2b",
            },
          }}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Box sx={{ px: 4, py: 2 }}>
          {/* Filter Section */}
          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: "16px",
              border: "1px solid #e0e0e0",
              bgcolor: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <Typography
              variant="h6"
              color="#333"
              fontFamily="Karbon"
              mb={3}
              fontWeight={600}
            >
              Filter Leads
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: 3,
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  color="#555"
                  fontFamily="Karbon"
                  fontSize="0.9rem"
                  mb={1}
                >
                  Start Date
                </Typography>
                <TextField
                  type="date"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: endDate || today, // Cannot be greater than end date or today
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Karbon",
                      bgcolor: "#fafafa",
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#ff6b35",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff6b35",
                      },
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  color="#555"
                  fontFamily="Karbon"
                  fontSize="0.9rem"
                  mb={1}
                >
                  End Date
                </Typography>
                <TextField
                  type="date"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: startDate, // Cannot be less than start date
                    max: today, // Cannot be greater than today
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Karbon",
                      bgcolor: "#fafafa",
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#ff6b35",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff6b35",
                      },
                    },
                  }}
                />
              </Box>
              <Box>
                <Typography
                  color="#555"
                  fontFamily="Karbon"
                  fontSize="0.9rem"
                  mb={1}
                >
                  Status
                </Typography>
                <TextField
                  select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  fullWidth
                  placeholder="Select Status"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontFamily: "Karbon",
                      bgcolor: "#fafafa",
                      borderRadius: "8px",
                      "&:hover fieldset": {
                        borderColor: "#ff6b35",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff6b35",
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="followed_up">Followed Up</MenuItem>
                  <MenuItem value="forwarded">Forwarded</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>

          {/* Total Count and Export Button */}
          {totalCount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography color="#666666" fontSize="1.1rem" fontFamily="Karbon">
                Total Leads: {totalCount}
              </Typography>
            </Box>
          )}

          {/* Table Container */}
          <Box
            sx={{
              borderRadius: "12px",
              border: "1px solid #f0e5e0",
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Karbon",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: "#f7d3c5",
                    color: "#666666",
                  }}
                >
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      width: "80px",
                    }}
                  >
                    Sr.No.
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                    }}
                  >
                    Name
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                    }}
                  >
                    Mobile
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                    }}
                  >
                    State
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                    }}
                  >
                    City
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                      width: "120px",
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                    }}
                  >
                    Workshop
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      fontSize: "0.95rem",
                      fontFamily: "Karbon",
                    }}
                  >
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody>
                {leads?.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      style={{ textAlign: "center", padding: "60px 16px" }}
                    >
                      <Typography
                        color="#666666"
                        fontSize="1.1rem"
                        mb={2}
                        fontFamily="Karbon"
                      >
                        No leads available
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  leads?.map((lead, index) => (
                    <tr
                      key={lead.id}
                      style={{
                        backgroundColor:
                          index % 2 === 1 ? "#fff5f2" : "#ffffff",
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff5f2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          index % 2 === 1 ? "#fff5f2" : "#ffffff";
                      }}
                    >
                      <td style={{ padding: "16px" }}>
                        <Chip
                          label={(page - 1) * pageSize + index + 1}
                          size="small"
                          sx={{
                            bgcolor: "#f7d3c5",
                            color: "#ff6b35",
                            fontFamily: "Karbon",
                          }}
                        />
                      </td>
                      <td style={{ padding: "16px" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {lead.customer_name}
                        </Typography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {lead.email || "-"}
                        </Typography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {lead.phone_number}
                        </Typography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {parseLocation(lead.state)}
                        </Typography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {parseLocation(lead.city)}
                        </Typography>
                      </td>
                      <td style={{ padding: "16px", textAlign: "center" }}>
                        <Chip
                          label={formatStatusLabel(lead.status || "pending")}
                          size="small"
                          sx={{
                            ...getStatusColor(lead.status || "pending"),
                            fontFamily: "Karbon",
                          }}
                        />
                      </td>
                      <td style={{ padding: "16px" }}>
                        <Typography color="#666666" fontFamily="Karbon">
                          {lead.workshop_name || "-"}
                        </Typography>
                      </td>
                      <td style={{ padding: "16px" }}>
                        <Typography
                          color="#666666"
                          fontFamily="Karbon"
                          fontSize="0.9rem"
                        >
                          {formatToIST(lead.created_at)}
                        </Typography>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>

          {/* Pagination Controls */}
          <div
            className="pagination-footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "12px 0",
            }}
          >
            <div
              className="rows-per-page"
              style={{ fontFamily: "Karbon", color: "#666666" }}
            >
              <label htmlFor="rowsPerPage">Rows per page:</label>
              <select
                id="rowsPerPage"
                value={pageSize}
                onChange={handlePageSizeChange}
                className="rows-select mx-2"
                style={{
                  fontFamily: "Karbon",
                  color: "#666666",
                  outlineColor: "#666666",
                  marginLeft: 8,
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div
              className="pagination-controls"
              style={{ fontFamily: "Karbon", color: "#666666" }}
            >
              <span className="mx-2">
                Showing {startIndex + 1} - {endIndex} of {totalCount}
              </span>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="pagination-btn"
                style={{ marginRight: 8 }}
              >
                Prev
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const firstPage = Math.max(
                  1,
                  Math.min(page - 2, totalPages - 4)
                );
                const pageNum = firstPage + i;
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`pagination-btn ${
                      pageNum === page ? "active" : ""
                    }`}
                    style={{
                      margin: "0 2px",
                      minWidth: "32px",
                      textAlign: "center",
                      fontWeight: pageNum === page ? "bold" : "normal",
                      backgroundColor: pageNum === page ? "#ff6b35" : undefined,
                      color: pageNum === page ? "#fff" : "#666666",
                      borderRadius: "4px",
                      border: "1px solid #ff6b35",
                      cursor: "pointer",
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="pagination-btn"
                style={{ marginLeft: 8 }}
              >
                Next
              </button>
            </div>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default LeadGeneration;